import React, { memo, useCallback, useEffect, useState } from 'react';
import { Monitor, Smartphone, ShieldOff, LogOut, AlertTriangle } from 'lucide-react';
import httpclient from 'libs/Axios';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { error, success } from 'core/components/Notify';

interface Session {
  USER_LOGIN_ID: number;
  DEVICE_TYPE: string;
  DEVICE_INFO: string;
  IP_ADDRESS: string;
  ROLE: string;
  LOCATION: string;
  LOGIN_DATETIME: string;
  ACTIVITY_DATETIME: string;
}

function parseDeviceInfo(raw: string, deviceType: string): string {
  if (deviceType === 'ANDROID') return 'Android';
  if (deviceType === 'IOS') return 'iOS';

  if (!raw) return 'Web Browser';
  if (!raw.includes('Mozilla/')) return raw;

  let browser = 'Browser';
  if (raw.includes('Edg/')) browser = 'Edge';
  else if (raw.includes('OPR/') || raw.includes('Opera/')) browser = 'Opera';
  else if (raw.includes('Firefox/')) browser = 'Firefox';
  else if (raw.includes('Chrome/')) browser = 'Chrome';
  else if (raw.includes('Safari/')) browser = 'Safari';

  let os = '';
  if (raw.includes('Windows NT')) os = 'Windows';
  else if (raw.includes('Mac OS X')) os = 'macOS';
  else if (raw.includes('Linux')) os = 'Linux';

  return os ? `${browser} on ${os}` : browser;
}

function formatDate(dt: string): string {
  if (!dt) return '—';
  return new Date(dt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatRelative(dt: string): string {
  if (!dt) return '—';
  const diff = Date.now() - new Date(dt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(dt);
}

const DeviceIcon = ({ type, info }: { type: string; info: string }) => {
  const combined = (type + info).toLowerCase();
  if (
    combined.includes('android') ||
    combined.includes('ios') ||
    combined.includes('iphone') ||
    combined.includes('ipad') ||
    combined.includes('mobile') ||
    combined.includes('phone')
  ) {
    return <Smartphone className="w-4 h-4" />;
  }
  return <Monitor className="w-4 h-4" />;
};

export const ActiveSessions = memo(() => {
  const authData = useAuth();
  const currentLoginId = authData?.USER_LOGIN_ID;

  const [sessions, setSessions] = useState<Session[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [loggingOut, setLoggingOut] = useState<number | null>(null);
  const [confirmingLogout, setConfirmingLogout] = useState<number | null>(null);
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const res = await httpclient().get('auth/sessions');
      const list: Session[] = res?.data?.data?.SESSIONS ?? [];
      list.sort(
        (a, b) =>
          (b.USER_LOGIN_ID === currentLoginId ? 1 : 0) -
          (a.USER_LOGIN_ID === currentLoginId ? 1 : 0),
      );
      setSessions(list);
      setTotalCount(res?.data?.data?.COUNT ?? list.length);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [currentLoginId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleLogout = async (session: Session) => {
    setLoggingOut(session.USER_LOGIN_ID);
    setConfirmingLogout(null);
    try {
      await httpclient().post('auth/logout', { USER_LOGIN_ID: session.USER_LOGIN_ID });
      success('Session signed out', true);
      setSessions(prev => prev.filter(s => s.USER_LOGIN_ID !== session.USER_LOGIN_ID));
      setTotalCount(prev => Math.max(0, prev - 1));
    } catch {
      error('Failed to sign out session', true);
    } finally {
      setLoggingOut(null);
    }
  };

  const handleLogoutAll = async () => {
    setLoggingOutAll(true);
    setConfirmLogoutAll(false);
    const others = sessions.filter(s => s.USER_LOGIN_ID !== currentLoginId);
    let count = 0;
    for (const session of others) {
      try {
        await httpclient().post('auth/logout', { USER_LOGIN_ID: session.USER_LOGIN_ID });
        count++;
        setSessions(prev => prev.filter(s => s.USER_LOGIN_ID !== session.USER_LOGIN_ID));
        setTotalCount(prev => Math.max(0, prev - 1));
      } catch {
      }
    }
    if (count > 0) {
      success(`Signed out ${count} other ${count === 1 ? 'session' : 'sessions'}`, true);
    }
    setLoggingOutAll(false);
  };

  const otherSessionsCount = sessions.filter(s => s.USER_LOGIN_ID !== currentLoginId).length;
  const busy = !!loggingOut || loggingOutAll;


  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse h-[68px] rounded-xl border border-neutral-40 bg-neutral-20" />
        ))}
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <AlertTriangle className="w-9 h-9 text-secondary-orange-light opacity-60" />
        <p className="text-sm font-medium text-neutral-70">Couldn't load sessions</p>
        <button onClick={() => fetchSessions()} className="text-sm text-primary-main hover:underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-neutral-60">
          Signed in on <span className="font-semibold text-neutral-80">{totalCount}</span>{' '}
          {totalCount === 1 ? 'device' : 'devices'}
        </p>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {otherSessionsCount > 0 && !loggingOutAll && (
            confirmLogoutAll ? (
              <div className="flex items-center gap-2 bg-secondary-red-30 border border-secondary-red/20 rounded-lg px-3 py-1.5">
                <span className="text-xs text-secondary-red font-medium">
                  Sign out {otherSessionsCount} other {otherSessionsCount === 1 ? 'device' : 'devices'}?
                </span>
                <button
                  onClick={handleLogoutAll}
                  className="text-xs font-bold text-secondary-red hover:opacity-70 transition-opacity"
                >
                  Yes
                </button>
                <span className="text-neutral-50 text-xs">·</span>
                <button
                  onClick={() => setConfirmLogoutAll(false)}
                  className="text-xs text-neutral-60 hover:text-neutral-80 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmLogoutAll(true)}
                disabled={busy}
                className="text-xs text-secondary-red border border-secondary-red/30 hover:bg-secondary-red-30 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors font-medium"
              >
                Sign out all other devices
              </button>
            )
          )}

          {loggingOutAll && (
            <span className="flex items-center gap-1.5 text-xs text-neutral-60">
              <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Signing out…
            </span>
          )}
        </div>
      </div>

      {/* Empty state */}
      {sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-neutral-50">
          <ShieldOff className="w-9 h-9 opacity-40" />
          <p className="text-sm text-neutral-60">No active sessions</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {sessions.map(session => {
            const isCurrent = session.USER_LOGIN_ID === currentLoginId;
            const isLoggingOutThis = loggingOut === session.USER_LOGIN_ID;
            const isConfirming = confirmingLogout === session.USER_LOGIN_ID;
            const deviceName = parseDeviceInfo(session.DEVICE_INFO, session.DEVICE_TYPE);

            return (
              <div
                key={session.USER_LOGIN_ID}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
                  isCurrent
                    ? 'border-primary-30 bg-primary-10'
                    : 'border-neutral-40 bg-white hover:bg-neutral-20'
                }`}
              >
                <div className={`flex-shrink-0 p-2 rounded-lg ${isCurrent ? 'bg-primary-20 text-primary-main' : 'bg-neutral-30 text-neutral-60'}`}>
                  <DeviceIcon type={session.DEVICE_TYPE} info={session.DEVICE_INFO} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-neutral-80 text-sm leading-tight truncate max-w-[200px]">
                      {deviceName}
                    </span>
                    {isCurrent && (
                      <span className="text-[10px] font-semibold bg-primary-main text-white px-2 py-0.5 rounded-full tracking-wide uppercase leading-none">
                        This device
                      </span>
                    )}
                    {session.ROLE && (
                      <span className="text-[11px] text-neutral-60 bg-neutral-30 px-2 py-0.5 rounded-full leading-none">
                        {session.ROLE}
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-neutral-60 mt-0.5">
                    Last active {formatRelative(session.ACTIVITY_DATETIME)}
                  </p>

                  <p className="text-[11px] text-neutral-50 mt-0.5 truncate">
                    {[
                      session.LOCATION || 'Unknown location',
                      session.LOGIN_DATETIME && `Signed in ${formatDate(session.LOGIN_DATETIME)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>

                {!isCurrent && (
                  isConfirming ? (
                    <div className="flex-shrink-0 flex items-center gap-2">
                      <span className="text-xs text-neutral-60 whitespace-nowrap">Sign out?</span>
                      <button
                        onClick={() => handleLogout(session)}
                        className="text-xs font-bold text-secondary-red hover:opacity-70 transition-opacity"
                      >
                        Yes
                      </button>
                      <span className="text-neutral-40 text-xs">·</span>
                      <button
                        onClick={() => setConfirmingLogout(null)}
                        className="text-xs text-neutral-60 hover:text-neutral-80 transition-colors"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmingLogout(session.USER_LOGIN_ID)}
                      disabled={busy}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-neutral-60 border border-neutral-40 hover:border-secondary-red hover:text-secondary-red rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {isLoggingOutThis ? (
                        <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                      ) : (
                        <LogOut className="w-3.5 h-3.5" />
                      )}
                      {isLoggingOutThis ? 'Signing out…' : 'Sign out'}
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
